import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Stack, 
  IconButton 
} from '@mui/material';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import FormatSizeIcon from '@mui/icons-material/FormatSize'; // Generic icon for AA
import CustomOptions from '../../Components/CustomOptions';
import { COLORS } from '../../Components/colors';


// --- Reusable Component 1: The Blue Outline Selection Card ---
const OptionCard = ({ label, selected, onClick, endIcon, showRadio = false, disabled = false }) => {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: selected ? `1px solid ${COLORS.primaryDark}` : `1px solid ${COLORS.borderLight}`,
        borderRadius: 1,
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: '100px',
        color: disabled ? COLORS.textLightGrey : (selected ? COLORS.primaryDark : COLORS.textDarkGrey),
        fontWeight: 500,
        fontSize: 11,
        backgroundColor: disabled ? COLORS.bgLight : COLORS.bgWhite,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: disabled ? COLORS.borderLight : COLORS.primaryDark,
        }
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 11 }}>
        {label}
      </Typography>
      
      <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
        {/* If it has a specific icon (like 'AA'), show that instead */}
        {endIcon && (
           <Box sx={{ 
             border: `1px solid ${COLORS.border}`, 
             borderRadius: '4px', 
             p: '2px', 
             lineHeight: 0,
             color: COLORS.textIcon,
           }}>
             {endIcon}
           </Box>
        )}
        {showRadio && (
          selected 
            ? <TripOriginIcon sx={{ fontSize: 18, color: COLORS.primaryDark }} disableRipple/>
            : <TripOriginIcon sx={{ fontSize: 18, color: COLORS.textWhite }} disableRipple/>
        )}
      </Stack>
    </Box>
  );
};




// --- Main Page Component ---
const FormatSettings = ({ displaySettings, setDisplaySettings, canUpdate = true, onPermissionDenied = null }) => {
  // State
  const { textFormat, negativeFormat, showRedNegative, withoutCents, excludeZero } = displaySettings || {
    textFormat: 'default',
    negativeFormat: 'trailing',
    showRedNegative: true,
    withoutCents: false,
    excludeZero: false
  };

  const setTextFormat = (val) => {
    if (!canUpdate) return onPermissionDenied && onPermissionDenied();
    setDisplaySettings(prev => ({ ...prev, textFormat: val }));
  };
  const setNegativeFormat = (val) => {
    if (!canUpdate) return onPermissionDenied && onPermissionDenied();
    setDisplaySettings(prev => ({ ...prev, negativeFormat: val }));
  };
  const setShowRedNegative = (val) => {
    if (!canUpdate) return onPermissionDenied && onPermissionDenied();
    setDisplaySettings(prev => ({ ...prev, showRedNegative: val }));
  };
  const setWithoutCents = (val) => {
    if (!canUpdate) return onPermissionDenied && onPermissionDenied();
    setDisplaySettings(prev => ({ ...prev, withoutCents: val }));
  };
  const setExcludeZero = (val) => {
    if (!canUpdate) return onPermissionDenied && onPermissionDenied();
    setDisplaySettings(prev => ({ ...prev, excludeZero: val }));
  };
  
  // Custom Icon for 'Upper' and 'Lower' simulation
  const AAIcon = ({ type }) => (
    <Typography sx={{ 
      fontSize: 11, 
      fontWeight: 'bold', 
      textTransform: type === 'upper' ? 'uppercase' : 'lowercase' 
    }}>
      {type === 'upper' ? 'AA' : 'Aa'}
    </Typography>
  );

  return (
    <Box sx={{ p: 1, backgroundColor: COLORS.bgWhite, fontFamily: 'Roboto, sans-serif' }}>
      
      {/* 1. TOP ROW: Display Text Format */}
      <Stack direction="row" alignItems="center" spacing={4} sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ minWidth: "auto !important", color: COLORS.textPrimary, fontSize: 10 }}>
          Display text format
        </Typography>
        <Stack direction="row" spacing={4}>
          <OptionCard 
            label="Default" 
            selected={textFormat === 'default'} 
            onClick={() => setTextFormat('default')}
            showRadio={true} 
          />
          <OptionCard 
            label="Upper" 
            selected={textFormat === 'upper'} 
            onClick={() => setTextFormat('upper')}
            endIcon={<AAIcon type="upper"/>} 
            showRadio={true}
          />
          <OptionCard 
            label="Lower" 
            selected={textFormat === 'lower'} 
            onClick={() => setTextFormat('lower')}
            endIcon={<AAIcon type="lower"/>} 
            showRadio={true}
          />
        </Stack>
      </Stack>

      {/* 2. BOTTOM SECTION: Grid Layout */}
      <Grid container spacing={1}>
        
        {/* Left Box: Show Negative Figures */}
        <Grid item xs={6} md={7}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ minWidth: 140, color: COLORS.textPrimary, fontSize: 11 }}>
                Show Negative Figures
              </Typography>
              
              <Stack direction="row" spacing={2} fontSize={11}>
                <OptionCard 
                  label="Normal" 
                  selected={negativeFormat === 'normal'} 
                  onClick={() => setNegativeFormat('normal')}
                  showRadio={true}
                />
                <OptionCard 
                  label="Parentheses" 
                  selected={negativeFormat === 'parentheses'} 
                  onClick={() => setNegativeFormat('parentheses')}
                  showRadio={true}
                  // Parentheses usually doesn't show a radio in this specific UI unless selected, but let's keep it consistent or clean
                />
                <OptionCard 
                  label="With a trailing Minus" 
                  selected={negativeFormat === 'trailing'} 
                  onClick={() => setNegativeFormat('trailing')}
                  showRadio={true}
                />
              </Stack>
            </Stack>

            {/* Green Toggle inside Left Box */}
            <Box sx={{ pl: '145px' , width: '100%' }}> {/* Indent to align with buttons */}
              <CustomOptions 
                type="Show Negative figures in Red" 
                selected={showRedNegative} 
                onClick={() => setShowRedNegative(!showRedNegative)} 
                width="60%"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Right Box: Show Figures */}
        <Grid item xs={6} md={5}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 1 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1} >
              <Typography variant="body2" sx={{ minWidth: 100, pt: 1.5, color: COLORS.textPrimary, fontSize: 11 }}>
                Show Figures
              </Typography>
              
              <Stack spacing={1} sx={{ mt: 2, gap: 2 }}>
                <CustomOptions 
                  type="Without Decimal" 
                  selected={withoutCents} 
                  onClick={() => setWithoutCents(!withoutCents)} 
                  width="100%"
                />
                
                <CustomOptions 
                  type="Exclude Zero Values" 
                  selected={excludeZero} 
                  onClick={() => setExcludeZero(!excludeZero)} 
                  width="100%"
                />
              </Stack>
            </Stack>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

export default React.memo(FormatSettings);
